/**
 * RosCompletenessCard Component
 *
 * Displays ROS (Review of Systems) completeness for a patient or aggregate.
 * Shows the number of documented organ systems out of 14 total.
 *
 * @example
 * ```tsx
 * <RosCompletenessCard
 *   documented={8}
 *   total={14}
 *   percentage={57.1}
 *   isComplete={false}
 * />
 * ```
 */

import React from 'react';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';

interface RosCompletenessCardProps {
  /** Number of organ systems documented */
  documented: number;
  /** Total number of organ systems (always 14) */
  total: number;
  /** Completeness percentage */
  percentage: number;
  /** Whether all systems are documented */
  isComplete: boolean;
  /** Optional title override */
  title?: string;
  /** Optional CSS class */
  className?: string;
}

/**
 * Returns appropriate Bootstrap color class based on completeness
 */
const getCompletenessColor = (percentage: number): string => {
  if (percentage >= 100) return 'text-success';
  if (percentage >= 70) return 'text-warning';
  return 'text-danger';
};

/**
 * Returns progress bar color class based on completeness
 */
const getProgressBarColor = (percentage: number): string => {
  if (percentage >= 100) return 'bg-success';
  if (percentage >= 70) return 'bg-warning';
  return 'bg-danger';
};

const RosCompletenessCard: React.FC<RosCompletenessCardProps> = ({
  documented,
  total,
  percentage,
  isComplete,
  title = 'Revisión de Sistemas (ROS)',
  className = '',
}) => {
  const colorClass = getCompletenessColor(percentage);
  const progressBarColor = getProgressBarColor(percentage);
  const progressWidth = Math.min(percentage, 100);

  return (
    <div className={`card ${className}`}>
      <div className="card-header">
        <h4 className="card-title d-inline-block">{title}</h4>
        {isComplete && (
          <span className="badge bg-success float-end">
            <FeatherIcon icon="check-circle" className="me-1" />
            Completo
          </span>
        )}
      </div>
      <div className="card-body">
        <div className="row align-items-center">
          <div className="col-md-8">
            <div className="Completeness-score">
              <h2 className={` Completeness-percentage ${colorClass}`}>
                {percentage.toFixed(1)}%
              </h2>
              <p className="text-muted mb-0">
                {documented} de {total} sistemas documentados
              </p>
            </div>

            <div className="Completeness-progress mt-3">
              <div className="progress" style={{ height: '8px' }}>
                <div
                  className={`progress-bar ${progressBarColor}`}
                  role="progressbar"
                  style={{ width: `${progressWidth}%` }}
                  aria-valuenow={percentage}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
            </div>

            {!isComplete && (
              <p className="text-muted mt-2 mb-0">
                <small>
                  Faltan {total - documented} sistemas por documentar
                </small>
              </p>
            )}
          </div>

          <div className="col-md-4 text-center">
            <div
              className="Completeness-ring"
              style={{
                position: 'relative',
                width: '80px',
                height: '80px',
                margin: '0 auto',
              }}
            >
              <svg viewBox="0 0 36 36" className="circular-chart" style={{ width: '100%', height: '100%' }}>
                <path
                  className="circle-bg"
                  d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="#eee"
                  strokeWidth="3"
                />
                <path
                  className={`circle ${progressBarColor}`}
                  d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  strokeWidth="3"
                  strokeDasharray={`${percentage}, 100`}
                  strokeLinecap="round"
                />
                <text
                  x="18"
                  y="20.35"
                  className={`percentage ${colorClass}`}
                  textAnchor="middle"
                  fontSize="8"
                  fontWeight="bold"
                >
                  {Math.round(percentage)}%
                </text>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RosCompletenessCard;